import { useState } from 'react';
import { observer } from 'mobx-react';
import {
  Box,
  Button,
  Typography,
  Alert,
  TextField,
  IconButton,
  Tooltip,
  Paper,
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { AxiosError } from 'axios';
import { BookkeeperTypes } from '../constants';
import useCompanyId from '../hooks/useCompanyId';
import useIsSubscriptionActive from '../hooks/useIsSubscriptionActive';
import { useIntegrateBookkeepingMutation } from '../stores/FetcherStores/BookkeepingIntegrationFetcher';

const BookkeepingIntegration = observer(() => {
  const [integrationUrl, setIntegrationUrl] = useState<string | null>(null);
  const companyId = useCompanyId();
  const { mutateAsync: integrateBookkeeping, isLoading, error } = useIntegrateBookkeepingMutation();

  const hasPlus = useIsSubscriptionActive('PLUS');
  const hasBookkeeper = useIsSubscriptionActive('BOOKKEEPING');

  if (!hasPlus && !hasBookkeeper) {
    return null;
  }

  const handleIntegration = async (bookkeeper: string) => {
    await integrateBookkeeping({ bookkeeper, companyId })
      .then((response) => {
        setIntegrationUrl(response.webshopUrl || response);
      })
      .catch(() => {});
  };

  const copyToClipboard = async () => {
    if (integrationUrl) {
      await navigator.clipboard.writeText(integrationUrl);
    }
  };

  const openIntegrationUrl = () => {
    if (integrationUrl) {
      window.open(integrationUrl, '_blank');
    }
  };

  const errorMessage =
    error instanceof AxiosError ? error?.response?.data?.name || error?.message : error?.message;

  return (
    <Paper sx={{ mt: 3, p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
      <Typography variant="h6" gutterBottom>
        Aktivera bokföringsintegration
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Detta företag har en aktiv bokföringsprenumeration men ingen integration. Välj en
        bokföringstjänst för att komma igång.
      </Typography>

      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <Button
          variant="contained"
          onClick={() => handleIntegration(BookkeeperTypes.fortnox)}
          disabled={isLoading}
          sx={{ minWidth: 120 }}
        >
          {isLoading ? 'Aktiverar...' : 'Fortnox'}
        </Button>

        <Button
          variant="contained"
          onClick={() => handleIntegration(BookkeeperTypes.visma)}
          disabled={isLoading}
          sx={{ minWidth: 120 }}
        >
          {isLoading ? 'Aktiverar...' : 'Spiris'}
        </Button>
      </Box>

      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errorMessage}
        </Alert>
      )}

      {integrationUrl && (
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Integration URL:
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              fullWidth
              value={integrationUrl}
              InputProps={{ readOnly: true }}
              size="small"
            />
            <Tooltip title="Kopiera URL">
              <IconButton onClick={copyToClipboard} size="small">
                <ContentCopyIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title="Öppna URL">
              <IconButton onClick={openIntegrationUrl} size="small">
                <OpenInNewIcon />
              </IconButton>
            </Tooltip>
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            Kopiera denna URL och öppna den i en webbläsare för att slutföra integrationen.
          </Typography>
        </Box>
      )}
    </Paper>
  );
});

export default BookkeepingIntegration;
